import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  canEditSamplingFishData,
  canDeletePondDetail,
  handleLegacySessionExpired,
} from "../../components/content/axiosConfig";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import {
  formatDisplayDate,
  formatFishingSamplingFileLabel,
} from "../../utils/formatDisplayDate";

function pondInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "P";
}

const PondDetailViaPond = () => {
  const history = useHistory();
  const [pondDetails, setPondDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [entriesPerPage] = useState(10);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [fishSamples, setFishSamples] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const isInitialSearchMount = useRef(true);
  const canEditSampling = canEditSamplingFishData(getUserDetails());
  const canDeleteSampling = canDeletePondDetail(getUserDetails());
  const { id } = useParams();

  const formatWrAsPercent = (wr) => {
    if (wr === "" || wr === undefined || wr === null) return "";
    const num = parseFloat(wr);
    if (Number.isNaN(num)) return "";
    return (num * 100).toFixed(1) + "%";
  };

  const FetchPondDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondDetail/GetAllDetailsByPondId/${id}`, {
        params: { search: searchTerm },
      });
      setPondDetails(response.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to fetch pond details";
        showErrorToast(errorMessage);
      }
      console.error("Error fetching pond details:", error);
    }
  }, [id, searchTerm]);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  const FetchFishSamples = useCallback(async (pondDetailsList) => {
    const fishDataObj = {};
    await Promise.all(
      pondDetailsList.map(async (detail) => {
        try {
          const res = await axiosInstance.get(
            `/api/SamplingFishes/GetAllFishesBYPondDID/${detail.id}`
          );
          fishDataObj[detail.id] = res.data;
        } catch {
          fishDataObj[detail.id] = [];
        }
      })
    );
    setFishSamples(fishDataObj);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
    FetchPondDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, CheckExpiry]);

  useEffect(() => {
    if (pondDetails.length > 0) {
      FetchFishSamples(pondDetails);
    } else {
      setFishSamples({});
      setExpandedIds(new Set());
    }
  }, [pondDetails, FetchFishSamples]);

  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      FetchPondDetail();
    }, 800);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = pondDetails.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(pondDetails.length / entriesPerPage) || 1;
  const showFrom = pondDetails.length === 0 ? 0 : indexOfFirstEntry + 1;
  const showTo = Math.min(indexOfLastEntry, pondDetails.length);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleExpanded = (reportId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  const DeletePond = async (detailId) => {
    const result = await Swal.fire({
      title: "Delete this sampling session?",
      text: "Associated fish data may be affected. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#14532d",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/api/PondDetail/DeletePondDetail/${detailId}`);
        const updatedPonds = pondDetails.filter((pond) => pond.id !== detailId);
        setPondDetails(updatedPonds);
        showSuccessToast("Pond detail deleted successfully");
      } catch (error) {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
          const auth = handleLegacySessionExpired(error);
          showErrorToast(auth.message);
        } else {
          const errorMessage = error.response?.data?.message || "Failed to delete pond detail";
          showErrorToast(errorMessage);
        }
        console.error("Error deleting pond detail:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return [...Array(totalPages)].map((_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [totalPages, currentPage]);

  const speciesRows = (reportId) =>
    (fishSamples[reportId] || []).filter((fish) => fish.NumberOfFishes > 0);

  const renderSpeciesPanel = (reportId) => {
    const rows = speciesRows(reportId);
    return (
      <div className="donezo-sampling-species">
        <div className="donezo-sampling-species__head">
          <i className="fa fa-fish" aria-hidden />
          <span>Fish Species Details</span>
        </div>
        <div className="table-responsive">
          <table className="table align-middle mb-0 donezo-sampling-species__table">
            <thead>
              <tr>
                <th>Fish Species</th>
                <th className="text-center">Caught</th>
                <th className="text-center">Culled</th>
                <th className="text-center">Released</th>
                <th className="text-center">Avg. Wr %</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((fish, idx) => (
                  <tr key={fish.FishTypeID || idx}>
                    <td>
                      <span className="donezo-status-dot donezo-status-dot--natural">
                        <span className="donezo-status-dot__mark" aria-hidden />
                        {fish.FishTypeName}
                      </span>
                    </td>
                    <td className="text-center">{fish.NumberOfFishes}</td>
                    <td className="text-center">{fish.NumberOfFishesCulled}</td>
                    <td className="text-center">{fish.NumberOfFishesReleased}</td>
                    <td className="text-center">{formatWrAsPercent(fish.AverageWr)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="donezo-sampling-species__empty">
                    No fish data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRowActions = (report, alwaysVisible = false) => (
    <div className={`donezo-orders-actions${alwaysVisible ? " is-visible" : ""}`}>
      <button
        type="button"
        className="donezo-orders-actions__btn"
        title={expandedIds.has(report.id) ? "Hide species" : "Show species"}
        aria-label={expandedIds.has(report.id) ? "Hide species" : "Show species"}
        onClick={(e) => {
          e.stopPropagation();
          toggleExpanded(report.id);
        }}
      >
        <i
          className={`fa ${expandedIds.has(report.id) ? "fa-eye-slash" : "fa-eye"}`}
          aria-hidden
        />
      </button>
      {canEditSampling && (
        <Link
          to={`/Updatepd/${report.id}`}
          className="donezo-orders-actions__btn"
          title="Edit"
          aria-label="Edit sampling session"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="fa fa-pen" aria-hidden />
        </Link>
      )}
      {canDeleteSampling && (
        <button
          type="button"
          className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
          title="Delete"
          aria-label="Delete sampling session"
          onClick={(e) => {
            e.stopPropagation();
            DeletePond(report.id);
          }}
        >
          <i className="fa fa-trash" aria-hidden />
        </button>
      )}
    </div>
  );

  const emptyMessage = searchTerm
    ? "Try a different search, or clear the filter."
    : canEditSampling
      ? "Add your first fishing / sampling session for this pond."
      : "No sampling sessions to show yet.";

  const pondName =
    currentEntries[0]?.PondName || pondDetails[0]?.PondName || "";

  if (isLoading) return <Preloader />;

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-workflow-page donezo-fishing-data">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar">
          <h1 className="donezo-orders-toolbar__title">
            {windowWidth > 768 ? "Sampling Data" : "Sampling"}
          </h1>

          <label htmlFor="pond-sampling-search" className="visually-hidden">
            Search sampling data
          </label>
          <div className="donezo-orders-toolbar__search">
            <i className="fa fa-search" aria-hidden />
            <input
              id="pond-sampling-search"
              type="search"
              className="form-control"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="donezo-orders-toolbar__actions">
            {canEditSampling && (
              <Link
                to={`/pondSamplingUpload/${id}`}
                className="donezo-orders-toolbar__ghost"
              >
                <i className="fa fa-file-upload" aria-hidden />
                <span className="d-none d-md-inline">Upload</span>
              </Link>
            )}
            {canEditSampling && (
              <Link to={`/createpd/${id}`} className="donezo-orders-toolbar__primary">
                {windowWidth > 576 ? "Add Sampling" : "Add"}
              </Link>
            )}
          </div>
        </div>

        {pondName ? (
          <p className="donezo-page-fine-print mb-3" style={{ marginTop: "-6px" }}>
            {pondName}
          </p>
        ) : null}

        <div className="card border-0 shadow-sm mb-3 donezo-workflow-tabs">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item me-2">
              <Link to={`/updatePond/${id}`} className="nav-link px-3 py-2 rounded-top">
                <i className="fas fa-cog me-2" aria-hidden />
                General
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={`/pondFeedFertilization/${id}`}
                className="nav-link px-3 py-2 rounded-top"
              >
                <i className="fas fa-leaf me-2" aria-hidden />
                Feed &amp; Fertilization
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link to={`/pondStocking/${id}`} className="nav-link px-3 py-2 rounded-top">
                <i className="fas fa-water me-2" aria-hidden />
                Stocking Data
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={`/pondDetailViaPond/${id}`}
                className="nav-link px-3 py-2 rounded-top donezo-tab-active"
              >
                <i className="fas fa-chart-line me-2" aria-hidden />
                Fishing / Sampling Data
              </Link>
            </li>
          </ul>
        </div>

        <div className="donezo-orders-panel">
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table align-middle mb-0 donezo-orders-table">
                <thead>
                  <tr>
                    <th>Pond</th>
                    <th>Sampling File</th>
                    <th className="text-center">Fish</th>
                    <th className="text-center">Species</th>
                    <th>Updated</th>
                    <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {pondDetails.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-0 border-0">
                        <div className="donezo-empty-state">
                          <div className="donezo-empty-state__icon" aria-hidden>
                            <i className="fa fa-fish" />
                          </div>
                          <p className="donezo-empty-state__title">No sampling data found</p>
                          <p className="donezo-empty-state__text">{emptyMessage}</p>
                          {canEditSampling && !searchTerm && (
                            <Link
                              to={`/createpd/${id}`}
                              className="btn btn-primary rounded-pill px-4 mt-3 d-inline-flex align-items-center"
                            >
                              <i className="fa fa-plus me-2" aria-hidden />
                              Add Sampling
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentEntries.map((report) => {
                      const isOpen = expandedIds.has(report.id);
                      return (
                        <React.Fragment key={report.id}>
                          <tr
                            className={isOpen ? "is-expanded" : undefined}
                            onClick={() =>
                              canEditSampling && history.push(`/Updatepd/${report.id}`)
                            }
                            style={{ cursor: canEditSampling ? "pointer" : "default" }}
                          >
                            <td>
                              <div className="donezo-orders-pond">
                                <span className="donezo-orders-pond__avatar" aria-hidden>
                                  {pondInitial(report.PondName)}
                                </span>
                                <div className="donezo-orders-pond__meta">
                                  <span className="donezo-orders-pond__name">
                                    {report.PondName || "—"}
                                  </span>
                                  <span className="donezo-orders-pond__id">#{report.id}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="donezo-orders-pond__name">
                                {formatFishingSamplingFileLabel(report)}
                              </span>
                            </td>
                            <td className="text-center">{report.NoFishes ?? "—"}</td>
                            <td className="text-center">
                              <span className="donezo-status-dot donezo-status-dot--stocked">
                                <span className="donezo-status-dot__mark" aria-hidden />
                                {report.NoFishTypes ?? "—"}
                              </span>
                            </td>
                            <td className="donezo-orders-table__date">
                              {formatDisplayDate(report.updatedAt)}
                            </td>
                            <td
                              className="donezo-orders-table__actions-col"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {renderRowActions(report)}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="donezo-orders-expand-row">
                              <td colSpan={6} className="p-0 border-0">
                                {renderSpeciesPanel(report.id)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-md-none donezo-orders-panel__mobile">
            {pondDetails.length === 0 ? (
              <div className="donezo-empty-state donezo-empty-state--compact">
                <p className="donezo-empty-state__title">No sampling data found</p>
                <p className="donezo-empty-state__text">
                  {searchTerm ? "Try another search." : "No sessions to show."}
                </p>
                {canEditSampling && !searchTerm && (
                  <Link to={`/createpd/${id}`} className="btn btn-primary rounded-pill px-4 mt-3">
                    Add Sampling
                  </Link>
                )}
              </div>
            ) : (
              currentEntries.map((report) => (
                <div key={report.id} className="donezo-mobile-card">
                  <div className="donezo-mobile-card__head">
                    <div className="donezo-orders-pond">
                      <span className="donezo-orders-pond__avatar" aria-hidden>
                        {pondInitial(report.PondName)}
                      </span>
                      <div className="donezo-orders-pond__meta">
                        <h2 className="donezo-mobile-card__title mb-0">
                          {formatFishingSamplingFileLabel(report)}
                        </h2>
                        <span className="donezo-orders-pond__id">
                          {report.PondName || "—"} · #{report.id}
                        </span>
                      </div>
                    </div>
                    {renderRowActions(report, true)}
                  </div>
                  <div className="donezo-mobile-card__grid">
                    <div>
                      <span className="donezo-mobile-card__label">Fish</span>
                      <div className="donezo-mobile-card__value">{report.NoFishes ?? "—"}</div>
                    </div>
                    <div>
                      <span className="donezo-mobile-card__label">Species</span>
                      <div className="donezo-mobile-card__value">{report.NoFishTypes ?? "—"}</div>
                    </div>
                    <div className="donezo-mobile-card__full">
                      <span className="donezo-mobile-card__label">Updated</span>
                      <div className="donezo-mobile-card__value">
                        {formatDisplayDate(report.updatedAt)}
                      </div>
                    </div>
                  </div>
                  {expandedIds.has(report.id) && (
                    <div className="mt-3">{renderSpeciesPanel(report.id)}</div>
                  )}
                  {canEditSampling && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-pill mt-3 w-100"
                      onClick={() => history.push(`/Updatepd/${report.id}`)}
                    >
                      Open session
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {pondDetails.length > 0 && (
            <div className="donezo-orders-footer">
              <div className="donezo-orders-footer__meta">
                Showing {showFrom}-{showTo} from {pondDetails.length}
              </div>
              <ul className="donezo-orders-pagination">
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    aria-label="Previous page"
                  >
                    <i className="fa fa-chevron-left" aria-hidden />
                  </button>
                </li>
                {windowWidth < 768 ? (
                  <li>
                    <span className="donezo-orders-pagination__btn is-active">{currentPage}</span>
                  </li>
                ) : (
                  pageNumbers.map((page, idx) => {
                    const prev = pageNumbers[idx - 1];
                    const showEllipsis = prev != null && page - prev > 1;
                    return (
                      <li key={page} className="d-inline-flex align-items-center gap-1">
                        {showEllipsis && (
                          <span className="donezo-orders-pagination__ellipsis">…</span>
                        )}
                        <button
                          type="button"
                          className={`donezo-orders-pagination__btn${
                            page === currentPage ? " is-active" : ""
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })
                )}
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    aria-label="Next page"
                  >
                    <i className="fa fa-chevron-right" aria-hidden />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PondDetailViaPond;
